import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../../../../../../../../components/checkbox';
import { SelectOptionType } from '../../../../../../../../components/dialog/helper';
import { InputSelect } from '../../../../../../../../components/input/select';
import { ControlModeInternal, SourceThresholdInternal } from '../../../../../../../../data-access/gql-types/graphql';
import { useMeterConfigurationContext } from '../../context';
import { useMeterConfigErrorMessage } from '../../hooks/use-error-message';
import { ThresholdCondition } from '../../types';
import { isValidValue, sourceThresholdUnit } from '../../utils';
import { ActionPartForm } from '../configuration-form/action-part-form';
import { PricePartForm } from '../configuration-form/price-part-form';
import { ThresholdPartForm } from '../configuration-form/threshold-part-form';
import { GreaterChart } from '../hysteresis-chart/greater-chart';
import { LesserChart } from '../hysteresis-chart/lesser-chart';

type EventingRuleFormProps = {
  index: number;
  controlModeOptions: SelectOptionType<ControlModeInternal>[];
  sourceThresholdOptions: SelectOptionType<SourceThresholdInternal>[];
};

export const EventingRuleForm: React.FC<EventingRuleFormProps> = ({
  index,
  controlModeOptions,
  sourceThresholdOptions,
}) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');

  const { activeRuleIndex, formValues, form } = useMeterConfigurationContext();
  const currentRule = formValues.rules[activeRuleIndex];
  const { getErrorMessage } = useMeterConfigErrorMessage();
  const thresholdUnit = currentRule?.source ? sourceThresholdUnit[currentRule.source] : '';

  if (index !== activeRuleIndex) return null;

  return (
    <div className="max-width-desktop">
      <Controller
        name={`rules.${activeRuleIndex}.enabled`}
        control={form.control}
        render={({ field }) => (
          <Checkbox checked={field.value} reverse onChange={field.onChange}>
            {`${tc('active')} ${t('meterConfiguration.relay').toLowerCase()} ${activeRuleIndex + 1}`}
          </Checkbox>
        )}
      />
      <Controller
        name={`rules.${activeRuleIndex}.controlMode`}
        control={form.control}
        rules={{ required: tc('isRequired') as string }}
        render={({ field, fieldState: { error } }) => {
          const errorMessage = getErrorMessage(activeRuleIndex, 'controlMode', error);

          return (
            <InputSelect
              options={controlModeOptions}
              value={field.value}
              onChange={field.onChange}
              reverse
              sx={{ mb: 4 }}
              label={t('meterConfiguration.controlMode')}
              {...(errorMessage && { errorMessage })}
            />
          );
        }}
      />
      {currentRule.controlMode === ControlModeInternal.Threshold && (
        <>
          <hr className="m-b-24" />
          <ThresholdPartForm
            sourceThresholdOptions={sourceThresholdOptions}
            currentRule={currentRule}
            thresholdUnit={thresholdUnit}
          />
        </>
      )}
      {currentRule.controlMode === ControlModeInternal.DynamicPrice && (
        <>
          <hr className="m-b-24" />
          <PricePartForm currentRule={currentRule} />
        </>
      )}
      <hr className="m-b-24" />
      <ActionPartForm />
      {currentRule.thresholdCondition === ThresholdCondition.GreaterThan && isValidValue(currentRule.minValue) && (
        <>
          <hr className="m-b-24" />
          <GreaterChart
            value={currentRule.minValue as number}
            hysteresis={currentRule.hysteresis}
            thresholdUnit={thresholdUnit}
          />
        </>
      )}
      {currentRule.thresholdCondition === ThresholdCondition.LesserThan && isValidValue(currentRule.maxValue) && (
        <>
          <hr className="m-b-24" />
          <LesserChart
            value={currentRule.maxValue as number}
            hysteresis={currentRule.hysteresis}
            thresholdUnit={thresholdUnit}
          />
        </>
      )}
    </div>
  );
};
